import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

// Social media icons as SVG components
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const XTwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const socialIcons = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  tiktok: TikTokIcon,
  youtube: YouTubeIcon,
  x: XTwitterIcon,
};

const socialColors = {
  facebook: { bg: "bg-[#1877F2]", hover: "hover:bg-[#166FE5]" },
  instagram: { bg: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]", hover: "hover:opacity-90" },
  tiktok: { bg: "bg-black", hover: "hover:bg-gray-900" },
  youtube: { bg: "bg-[#FF0000]", hover: "hover:bg-[#CC0000]" },
  x: { bg: "bg-black", hover: "hover:bg-gray-900" },
};

const socialLabels = {
  facebook: "seguidores",
  instagram: "seguidores",
  tiktok: "seguidores",
  youtube: "suscriptores",
  x: "seguidores",
};

const formatNumber = (num) => {
  if (!num) return "0";
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toLocaleString();
};

export function SocialNetworksSection({ className = "" }) {
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNetworks();
  }, []);

  const fetchNetworks = async () => {
    try {
      const response = await axios.get(`${API}/config/social-networks`);
      setNetworks(response.data.networks || []);
    } catch (error) {
      console.error("Error fetching social networks:", error);
      // Fallback to defaults
      setNetworks([
        { id: "facebook", name: "Facebook", url: "https://facebook.com/cholutecanet", followers: 0 },
        { id: "instagram", name: "Instagram", url: "https://instagram.com/cholutecanet", followers: 0 },
        { id: "tiktok", name: "TikTok", url: "https://tiktok.com/@cholutecanet", followers: 0 },
        { id: "youtube", name: "YouTube", url: "https://youtube.com/@cholutecanet", followers: 0 },
        { id: "x", name: "X", url: "https://x.com/cholutecanet", followers: 0 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const totalFollowers = networks.reduce((sum, n) => sum + (n.followers || 0), 0);

  if (loading) {
    return (
      <section className={`py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 ${className}`} data-testid="social-networks-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
            Síguenos en Redes Sociales
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Únete a nuestra comunidad y mantente al día con las últimas noticias y contenido exclusivo
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
          {networks.map((network, index) => {
            const IconComponent = socialIcons[network.id] || XTwitterIcon;
            const colors = socialColors[network.id] || socialColors.x;
            const label = socialLabels[network.id] || "seguidores";
            
            return (
              <motion.a
                key={network.id}
                href={network.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`group relative flex flex-col items-center p-6 rounded-2xl ${colors.bg} ${colors.hover} transition-all duration-300 hover:scale-105 hover:shadow-2xl`}
                data-testid={`social-${network.id}`}
              >
                {/* Icon */}
                <div className="text-white mb-3 group-hover:scale-110 transition-transform">
                  <IconComponent />
                </div>
                
                {/* Name */}
                <span className="text-white font-medium text-sm mb-1">
                  {network.name}
                </span>
                
                {/* Followers count */}
                <div className="text-center">
                  <span className="text-white text-2xl font-bold block">
                    {formatNumber(network.followers)}
                  </span>
                  <span className="text-white/70 text-xs">
                    {label}
                  </span>
                </div>
                
                {/* External link indicator */}
                <ExternalLink className="absolute top-3 right-3 w-4 h-4 text-white/50 opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.a>
            );
          })}
        </div>

        {/* Total followers */}
        {totalFollowers > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="mt-10 text-center"
          >
            <p className="text-gray-400">
              Más de <span className="text-accent font-bold text-xl">{totalFollowers.toLocaleString()}</span> personas nos siguen
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
}

// Compact version for footer or sidebar
export function SocialNetworksCompact({ className = "" }) {
  const [networks, setNetworks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/config/social-networks`)
      .then(res => setNetworks(res.data.networks || []))
      .catch(() => {});
  }, []);

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {networks.map((network) => {
        const IconComponent = socialIcons[network.id] || XTwitterIcon;
        return (
          <a
            key={network.id}
            href={network.url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            title={`${network.name} - ${formatNumber(network.followers)}`}
            data-testid={`social-compact-${network.id}`}
          >
            <IconComponent />
          </a>
        );
      })}
    </div>
  );
}

export default SocialNetworksSection;
